/**
 * XeroStatusDots — Xero connection status indicator for the top bar.
 * Shows green (Connected) or red (Disconnected) per practice.
 * Click reveals a dropdown with per-practice status and link to Xero Reports.
 */
import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";

const XERO_PRACTICES = [
  { key: "apx",     name: "APX" },
  { key: "apx-hrb", name: "HRB" },
  { key: "apx-bbn", name: "BBN" },
] as const;

export default function XeroStatusDots() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: practiceStatuses = [] } = trpc.xero.practiceStatuses.useQuery(undefined, {
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const practices = XERO_PRACTICES.map((p) => {
    const pStatus = practiceStatuses.find((ps) => ps.practiceKey === p.key);
    const connected = pStatus?.connected ?? false;
    const lastRefresh = pStatus?.updatedAt
      ? new Date(pStatus.updatedAt).toLocaleString("en-US", {
          timeZone: "America/Los_Angeles",
          month: "short", day: "numeric",
          hour: "numeric", minute: "2-digit", hour12: true,
        }) + " PT"
      : null;
    return { ...p, connected, lastRefresh };
  });

  const anyDisconnected = practices.some(p => !p.connected);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold transition-all"
        style={{
          background: open ? "oklch(0.22 0.04 240 / 70%)" : "oklch(0.22 0.04 240 / 40%)",
          border: `1.5px solid ${anyDisconnected ? "#ef4444" : "#22c55e"}`,
          color: "oklch(0.85 0.02 240)",
        }}
        title="Xero connection status — click for details"
      >
        <span className="text-xs font-bold tracking-wider opacity-80">XERO</span>
        {practices.map((p) => (
          <span key={p.key} className="flex items-center gap-1.5">
            <span
              className="inline-block rounded-full shrink-0"
              style={{
                width: 10, height: 10,
                background: p.connected ? "#22c55e" : "#ef4444",
                boxShadow: p.connected ? "0 0 6px #22c55e" : "0 0 6px #ef4444",
              }}
            />
            <span className="text-xs font-semibold" style={{ color: "oklch(0.80 0.02 240)" }}>{p.name}</span>
          </span>
        ))}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute top-full right-0 mt-2 z-50 rounded-xl shadow-2xl overflow-hidden"
          style={{
            minWidth: 220,
            background: "oklch(0.12 0.02 240)",
            border: "1px solid oklch(0.72 0.14 75 / 40%)",
          }}
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "oklch(0.25 0.04 240)" }}>
            <p className="text-xs font-bold tracking-widest" style={{ color: "oklch(0.72 0.14 75)" }}>
              XERO CONNECTIONS
            </p>
          </div>
          <div className="divide-y" style={{ borderColor: "oklch(0.20 0.03 240)" }}>
            {practices.map((p) => (
              <div key={p.key} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block rounded-full shrink-0"
                    style={{
                      width: 10, height: 10,
                      background: p.connected ? "#22c55e" : "#ef4444",
                      boxShadow: p.connected ? "0 0 5px #22c55e" : "0 0 5px #ef4444",
                    }}
                  />
                  <span className="text-sm font-semibold" style={{ color: "oklch(0.90 0.02 240)" }}>
                    APX-{p.name}
                  </span>
                </div>
                <div className="text-right">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded"
                    style={{
                      background: p.connected ? "oklch(0.22 0.06 150 / 40%)" : "oklch(0.25 0.08 20 / 40%)",
                      color: p.connected ? "#22c55e" : "#ef4444",
                    }}
                  >
                    {p.connected ? "CONNECTED" : "DISCONNECTED"}
                  </span>
                  {p.lastRefresh && (
                    <p className="text-[10px] mt-0.5" style={{ color: "oklch(0.50 0.02 240)" }}>
                      Last: {p.lastRefresh}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 border-t" style={{ borderColor: "oklch(0.25 0.04 240)" }}>
            <button
              onClick={() => { setOpen(false); navigate("/xero-reports"); }}
              className="text-xs font-semibold w-full text-center transition-colors hover:opacity-80"
              style={{ color: "oklch(0.72 0.14 75)" }}
            >
              Go to Xero Reports →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
